import {Scale} from "chart.js";

export const syncPowerYAxisZeroPlugin = {
    id: 'syncPowerYAxisZero',
    _primaryScale(chart) {
        return chart.scales[chart.data?.datasets?.some(ds => ds.yAxisID === 'y1Aggregated') ? 'y1Aggregated' : 'y1'];
    },
    _hasFiniteDataValue(data) {
        return data?.some(value => {
            if (typeof value === 'number') {
                return isFinite(value);
            }
            return typeof value?.y === 'number' && isFinite(value.y);
        });
    },
    _hasVisiblePowerData(chart) {
        const powerScale = chart.scales?.power;
        if (!powerScale || powerScale.options?.display === false) {
            return false;
        }
        return chart.data?.datasets?.some(ds =>
            ds.yAxisID === 'power' && this._hasFiniteDataValue(ds.data)) === true;
    },
    _spansZero(scale) {
        return scale && scale.min < 0 && scale.max > 0;
    },
    _niceCeil(value: number) {
        if (!isFinite(value) || value <= 0) {
            return value;
        }
        const base = Math.pow(10, Math.floor(Math.log10(value)));
        const fraction = value / base;
        const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]
            .find(candidate => candidate >= fraction) ?? 10;
        return step * base;
    },
    _applySymmetricZeroBounds(scale) {
        if (!scale) {
            return;
        }
        const maxAbsoluteValue = Math.max(Math.abs(scale.min || 0), Math.abs(scale.max || 0));
        if (maxAbsoluteValue <= 0) {
            return;
        }
        const bound = this._niceCeil(maxAbsoluteValue);
        scale.min = -bound;
        scale.max = bound;
        scale.options.min = scale.min;
        scale.options.max = scale.max;
    },
    _roundTickValue(value: number) {
        return Number(value.toPrecision(12));
    },
    afterDataLimits(chart, args: { scale: Scale }) {
        const powerScale = args.scale;
        if (powerScale.id !== 'power') {
            return;
        }
        if (!this._hasVisiblePowerData(chart)) {
            return;
        }
        const primaryScale = this._primaryScale(chart);
        if (!primaryScale) {
            return;
        }
        const needsZeroSync = primaryScale.min < 0 || powerScale.min < 0;
        if (needsZeroSync) {
            if (this._spansZero(primaryScale)) {
                this._syncTargetZeroRatio(primaryScale, powerScale);
            } else {
                this._applySymmetricZeroBounds(primaryScale);
                this._applySymmetricZeroBounds(powerScale);
            }
        }
    },
    _syncTargetZeroRatio(referenceScale, targetScale) {
        const ratioBelowZero = -referenceScale.min / (referenceScale.max - referenceScale.min);
        if (ratioBelowZero <= 0 || ratioBelowZero >= 1) {
            return;
        }
        const targetMin = Math.min(targetScale.min, 0);
        const targetMax = Math.max(targetScale.max, 0);
        const multiplier = this._niceCeil(Math.max(
            targetMax / referenceScale.max,
            targetMin / referenceScale.min
        ));
        targetScale.min = referenceScale.min * multiplier;
        targetScale.max = referenceScale.max * multiplier;
        targetScale.options.min = targetScale.min;
        targetScale.options.max = targetScale.max;
    },
    afterBuildTicks(chart, args: { scale: Scale }) {
        const powerScale = args.scale;
        if (powerScale.id !== 'power' || !this._hasVisiblePowerData(chart)) {
            return;
        }
        const primaryScale = this._primaryScale(chart);
        if (!primaryScale?.ticks || primaryScale.ticks.length < 2) {
            return;
        }
        const primaryRange = primaryScale.max - primaryScale.min;
        const powerRange = powerScale.max - powerScale.min;
        if (primaryRange <= 0 || powerRange <= 0) {
            return;
        }
        powerScale.ticks = primaryScale.ticks.map(tick => ({
            value: this._roundTickValue(powerScale.min + ((tick.value - primaryScale.min) / primaryRange) * powerRange)
        }));
    }
};
